import React, { useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  EditOutlined,
  ReloadOutlined,
  StarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  AdminUpdateUserInput,
  UserListItemDto,
  UserRole,
  useAdminUpdateUser,
  useUsers,
} from '@shared/api/hooks/useUsers';

const STAFF_ROLES: UserRole[] = ['CLUB_ADMIN', 'CLUB_EDITOR', 'CLUB_SHOP_MANAGER', 'CLUB_SUPPORT'];

const ROLE_LABEL: Record<UserRole, string> = {
  FAN: 'Tifoso',
  CLUB_EDITOR: 'Editor',
  CLUB_SHOP_MANAGER: 'Shop manager',
  CLUB_SUPPORT: 'Supporto',
  CLUB_ADMIN: 'Admin',
};

const ROLE_COLOR: Record<UserRole, string> = {
  FAN: 'blue',
  CLUB_EDITOR: 'cyan',
  CLUB_SHOP_MANAGER: 'gold',
  CLUB_SUPPORT: 'purple',
  CLUB_ADMIN: 'red',
};

interface EditFormValues {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: UserRole;
}

export function StaffPage(): React.ReactElement {
  const [page, setPage] = useState(1);
  const [editUser, setEditUser] = useState<UserListItemDto | null>(null);
  const [editForm] = Form.useForm<EditFormValues>();

  // Fetch one role at a time → merge to list. Simpler than backend filter
  // 'role IN' for now. Each query cached by role so re-renders are cheap.
  // STAFF_ROLES è una costante di modulo (lunghezza/ordine mai variabili a
  // runtime), quindi l'hook count/order è stabile tra i render nonostante il
  // .map(); eslint non può verificarlo staticamente. Refactor futuro: una
  // singola query con filtro `role IN` lato backend eliminerebbe il pattern.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const queries = STAFF_ROLES.map((role) => useUsers({ role, limit: 100 }));
  const all = queries.flatMap((q) => q.data?.data ?? []);
  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);
  const refetchAll = (): void => {
    queries.forEach((q) => q.refetch());
  };

  const updateMutation = useAdminUpdateUser();

  const onSubmitEdit = async (): Promise<void> => {
    if (!editUser) return;
    const v = await editForm.validateFields();
    try {
      const input: AdminUpdateUserInput = {};
      if (v.firstName) input.firstName = v.firstName;
      if (v.lastName) input.lastName = v.lastName;
      if (v.email) input.email = v.email;
      if (v.role) input.role = v.role;
      await updateMutation.mutateAsync({ id: editUser.id, input });
      message.success('Staff aggiornato');
      setEditUser(null);
      refetchAll();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : msg ?? 'Errore');
    }
  };

  const openEdit = (u: UserListItemDto): void => {
    editForm.resetFields();
    editForm.setFieldsValue({
      firstName: u.displayName?.split(' ')[0],
      lastName: u.displayName?.split(' ').slice(1).join(' ') || undefined,
      email: u.email,
      role: u.role,
    });
    setEditUser(u);
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Staff
          </Typography.Title>
          <Tag>{all.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refetchAll} loading={isFetching}>
            Aggiorna
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">
        Membri del club con accesso al backoffice. Per aggiungere un nuovo
        membro: il tifoso si registra dall&apos;app, poi qui usa &quot;Modifica&quot; per
        promuoverlo al ruolo desiderato (CLUB_EDITOR / CLUB_SHOP_MANAGER /
        CLUB_SUPPORT / CLUB_ADMIN).
      </Typography.Paragraph>

      <Table<UserListItemDto>
        rowKey="id"
        size="small"
        loading={isLoading}
        dataSource={all}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: 20,
          total: all.length,
          onChange: setPage,
          showSizeChanger: false,
        }}
        columns={[
          {
            title: '',
            width: 50,
            render: () => <Avatar icon={<UserOutlined />} />,
          },
          {
            title: 'Nome',
            render: (_v, u) => u.displayName ?? <Typography.Text type="secondary">—</Typography.Text>,
          },
          {
            title: 'Email',
            dataIndex: 'email',
            ellipsis: true,
            responsive: ['lg'],
          },
          {
            title: 'Ruolo',
            dataIndex: 'role',
            width: 140,
            render: (v: UserRole) => <Tag color={ROLE_COLOR[v]}>{ROLE_LABEL[v]}</Tag>,
          },
          {
            title: 'Punti',
            dataIndex: 'totalPoints',
            width: 90,
            align: 'right',
            responsive: ['md'],
            render: (v: number) => v.toLocaleString('it-IT'),
          },
          {
            title: 'Iscritto',
            dataIndex: 'createdAt',
            width: 120,
            responsive: ['lg'],
            render: (v: string) => new Date(v).toLocaleDateString('it-IT'),
          },
          {
            title: 'Stato',
            width: 110,
            render: (_v, u) =>
              u.isBanned ? (
                <Tag color="red">Bannato</Tag>
              ) : u.isActive ? (
                <Tag color="green">Attivo</Tag>
              ) : (
                <Tag>Disattivo</Tag>
              ),
          },
          {
            title: '',
            width: 100,
            align: 'right',
            render: (_v, u) => (
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(u)}>
                Modifica
              </Button>
            ),
          },
        ]}
        locale={{
          emptyText: (
            <div style={{ padding: 32 }}>
              <StarOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
              <div style={{ marginTop: 8, color: '#8c8c8c' }}>
                Nessun membro staff. Vai in <strong>Tifosi</strong> e promuovi un utente con
                &quot;Modifica&quot;.
              </div>
            </div>
          ),
        }}
      />

      <Modal
        title={`Modifica staff: ${editUser?.displayName ?? editUser?.email}`}
        open={editUser !== null}
        onCancel={() => setEditUser(null)}
        onOk={onSubmitEdit}
        okText="Salva"
        confirmLoading={updateMutation.isPending}
        destroyOnHidden
        width={580}
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          <Space.Compact block>
            <Form.Item name="firstName" label="Nome" style={{ flex: 1 }}>
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="lastName" label="Cognome" style={{ flex: 1, marginLeft: 12 }}>
              <Input maxLength={100} />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Ruolo" rules={[{ required: true }]}>
            <Select
              options={Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
